import type {Result} from 'framework';
import type {
  MeasurementSeriesRange,
  SaveSessionInput,
  UpdateBodyMetricPatch,
} from '../application/ports/body-measurements.repository';
import type {CreateBodyMetric} from '../application/use-cases/create-body-metric';
import type {DeleteBodyMetric} from '../application/use-cases/delete-body-metric';
import type {DeleteMeasurementEntry} from '../application/use-cases/delete-measurement-entry';
import type {GetMeasurementSeries} from '../application/use-cases/get-measurement-series';
import type {GetMeasurementSession} from '../application/use-cases/get-measurement-session';
import type {GetMeasurementsOverview} from '../application/use-cases/get-measurements-overview';
import type {ListBodyMetrics} from '../application/use-cases/list-body-metrics';
import type {SaveMeasurementSession} from '../application/use-cases/save-measurement-session';
import type {UpdateBodyMetric} from '../application/use-cases/update-body-metric';
import type {UpdateMeasurementEntry} from '../application/use-cases/update-measurement-entry';
import type {BodyMetric, BodyMetricId, BodyMetricWithEntryCount} from '../domain/body-metric';
import type {BodyMeasurementsError} from '../domain/errors';
import type {MeasurementEntry, MeasurementEntryId} from '../domain/measurement-entry';
import type {MetricOverviewItem} from '../domain/metric-overview-item';

export class BodyMeasurementsController {
  constructor(
    private readonly listBodyMetrics: ListBodyMetrics,
    private readonly createBodyMetric: CreateBodyMetric,
    private readonly updateBodyMetric: UpdateBodyMetric,
    private readonly deleteBodyMetric: DeleteBodyMetric,
    private readonly getMeasurementsOverview: GetMeasurementsOverview,
    private readonly getMeasurementSession: GetMeasurementSession,
    private readonly saveMeasurementSession: SaveMeasurementSession,
    private readonly getMeasurementSeries: GetMeasurementSeries,
    private readonly updateMeasurementEntry: UpdateMeasurementEntry,
    private readonly deleteMeasurementEntry: DeleteMeasurementEntry,
  ) {}

  listMetrics(): Promise<Result<readonly BodyMetricWithEntryCount[], BodyMeasurementsError>> {
    return this.listBodyMetrics.execute();
  }

  createMetric(name: string): Promise<Result<BodyMetric, BodyMeasurementsError>> {
    return this.createBodyMetric.execute({name});
  }

  updateMetric(
    metricId: BodyMetricId,
    patch: UpdateBodyMetricPatch,
  ): Promise<Result<BodyMetric, BodyMeasurementsError>> {
    return this.updateBodyMetric.execute({metricId, patch});
  }

  deleteMetric(metricId: BodyMetricId): Promise<Result<void, BodyMeasurementsError>> {
    return this.deleteBodyMetric.execute({metricId});
  }

  overview(): Promise<Result<readonly MetricOverviewItem[], BodyMeasurementsError>> {
    return this.getMeasurementsOverview.execute();
  }

  session(measuredOn: string): Promise<Result<readonly MeasurementEntry[], BodyMeasurementsError>> {
    return this.getMeasurementSession.execute({measuredOn});
  }

  saveSession(
    input: SaveSessionInput,
  ): Promise<Result<readonly MeasurementEntry[], BodyMeasurementsError>> {
    return this.saveMeasurementSession.execute(input);
  }

  series(
    metricId: BodyMetricId,
    range: MeasurementSeriesRange,
  ): Promise<Result<readonly MeasurementEntry[], BodyMeasurementsError>> {
    return this.getMeasurementSeries.execute({metricId, range});
  }

  updateEntry(
    entryId: MeasurementEntryId,
    valueCm: number,
  ): Promise<Result<MeasurementEntry, BodyMeasurementsError>> {
    return this.updateMeasurementEntry.execute({entryId, valueCm});
  }

  deleteEntry(entryId: MeasurementEntryId): Promise<Result<void, BodyMeasurementsError>> {
    return this.deleteMeasurementEntry.execute({entryId});
  }
}
